import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import { ModalBody, UtilityModal } from './Modal'
import { ToastNotification } from './Notifications/Toast'
import { StopJourney } from '../modules/Campaigns/graphql/mutations/journey'
import client from '../graphql/client'

const showStopError = (error: unknown) => {
  const body =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && 'message' in error
        ? String((error as { message: string }).message)
        : 'Failed to stop automation. Please try again.'

  ToastNotification({
    id: Date.now(),
    icon: 'alert',
    color: 'danger',
    body,
  })
}

export type JourneyStopOutcome = 'PAUSE_FINISH' | 'STOP_REMOVE' | 'PAUSE_FREEZE'

const OUTCOME_OPTIONS: {
  value: JourneyStopOutcome
  titleId: string
  titleDefault: string
  descriptionId: string
  descriptionDefault: string
}[] = [
  {
    value: 'PAUSE_FINISH',
    titleId: 'modal.stopJourney.pauseFinish.title',
    titleDefault: 'Let them finish',
    descriptionId: 'modal.stopJourney.pauseFinish.description',
    descriptionDefault: 'They continue and complete the flow as normal.',
  },
  {
    value: 'STOP_REMOVE',
    titleId: 'modal.stopJourney.stopRemove.title',
    titleDefault: 'Remove them',
    descriptionId: 'modal.stopJourney.stopRemove.description',
    descriptionDefault: "They exit now and get no more emails. They won't re-enter if you reactivate.",
  },
  {
    value: 'PAUSE_FREEZE',
    titleId: 'modal.stopJourney.pauseFreeze.title',
    titleDefault: 'Freeze them',
    descriptionId: 'modal.stopJourney.pauseFreeze.description',
    descriptionDefault: 'They stop where they are, and resume from that step when you reactivate.',
  },
]

interface StopJourneyModalProps {
  handleClose: () => void
  journeyId: number
  journeyName?: string
  usersInFlow?: number
  onSuccess?: (outcome: JourneyStopOutcome) => void
}

export function StopJourneyModal({ handleClose, journeyId, journeyName, usersInFlow = 0, onSuccess }: StopJourneyModalProps) {
  const [outcome, setOutcome] = useState<JourneyStopOutcome>('PAUSE_FINISH')
  const [stopJourney, { loading }] = useMutation(StopJourney, {
    refetchQueries: ['getCampaignFeed', 'fetchJourney'],
  })

  const peopleLabel = usersInFlow === 1 ? 'person' : 'people'

  return (
    <ModalBody
      title={<FormattedMessage id="modal.stopJourney.title" defaultMessage="Stop automation" />}
      description={
        journeyName ? (
          <FormattedMessage
            id="modal.stopJourney.descriptionNamed"
            defaultMessage="No new contacts will enter either way. Choose what happens to the {count} {peopleLabel} already in {name}:"
            values={{
              count: usersInFlow,
              peopleLabel,
              name: journeyName,
              bold: (chunks) => <strong className="font-semibold text-gray-900">{chunks}</strong>,
            }}
          />
        ) : (
          <FormattedMessage
            id="modal.stopJourney.description"
            defaultMessage="No new contacts will enter either way. Choose what happens to the {count} {peopleLabel} already in this automation:"
            values={{
              count: usersInFlow,
              peopleLabel,
              bold: (chunks) => <strong className="font-semibold text-gray-900">{chunks}</strong>,
            }}
          />
        )
      }
      action="primary"
      handleClose={handleClose}
      closeOnSubmit={false}
      showDescriptionChildren={true}
      buttons={[
        {
          label: <FormattedMessage id="modal.stopJourney.confirm" defaultMessage="Stop automation" />,
          action: 'danger',
          disabled: loading,
          onClick: async () => {
            try {
              const { data, errors } = await stopJourney({ variables: { id: journeyId, outcome } })

              if (errors?.length) {
                showStopError(errors[0].message)
                return
              }

              const payload = data?.stopJourney
              if (payload?.success) {
                onSuccess?.(outcome)
                handleClose()
                return
              }

              showStopError(payload?.error)
            } catch (err) {
              showStopError(err)
            }
          },
        },
      ]}
    >
      <div className="flex flex-col gap-3">
        {OUTCOME_OPTIONS.map((option) => (
          <label
            key={option.value}
            className={`flex gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
              outcome === option.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="stopOutcome"
              value={option.value}
              checked={outcome === option.value}
              onChange={() => setOutcome(option.value)}
              className="mt-1"
            />
            <div>
              <div className="text-sm font-medium text-gray-900">
                <FormattedMessage id={option.titleId} defaultMessage={option.titleDefault} />
              </div>
              <div className="text-xs text-gray-900 mt-1">
                <FormattedMessage id={option.descriptionId} defaultMessage={option.descriptionDefault} />
              </div>
            </div>
          </label>
        ))}
      </div>
    </ModalBody>
  )
}

export function showStopJourneyModal({
  journeyId,
  journeyName,
  usersInFlow = 0,
  onSuccess,
}: {
  journeyId: number
  journeyName?: string
  usersInFlow?: number
  onSuccess?: (outcome: JourneyStopOutcome) => void
}) {
  if (!usersInFlow) {
    client.mutate({
        mutation: StopJourney,
        variables: { id: journeyId, outcome: 'PAUSE_FINISH' },
        refetchQueries: ['getCampaignFeed', 'fetchJourney'],
      }).then(({ data, errors }) => {
        if (errors?.length) { 
          showStopError(errors[0].message) 
          return
        }
        const payload = data?.stopJourney
        if (payload?.success) {
          onSuccess?.('PAUSE_FINISH')
          return
        }
        showStopError(payload?.error)
      })
      .catch(showStopError)
    return
  }

  UtilityModal({
    id: `stop-journey-${journeyId}`,
    width: 520,
    body: (props) => (
      <StopJourneyModal
        {...props}
        journeyId={journeyId}
        journeyName={journeyName}
        usersInFlow={usersInFlow}
        onSuccess={onSuccess}
      />
    ),
  })
}
